/**
 * CLI configuration — non-secret metadata at ~/.wayai/config.json.
 *
 * Tokens themselves live in the OS keyring (see token-store.ts). This file
 * holds only metadata that survives a logout/login cycle gracefully if needed.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as os from 'node:os';

/**
 * Non-secret CLI configuration. Token fields removed in v0.4.0 — they now live
 * in the OS keyring. token_expires_at is metadata (timestamp, not a secret) and
 * stays here so the refresh path can decide when to refresh without unlocking
 * the keyring just to read the expiry.
 */
export interface CliConfig {
  api_url: string;
  /** ISO 8601 expiry of the OAuth access token (metadata, not secret). */
  token_expires_at?: string;
  /**
   * Report-action nudge dedup keys the user has already been nudged about, so the login/status
   * reminder fires once per notifiable event. Shipped keys are `<report_id>:shipped:<contest_count>`
   * (one per shipment — a re-shipped fix re-nudges, matching the re-firing shipped email); dismissed
   * keys are `<report_id>:dismissed` (once-ever, matching the once-ever dismissed email). Capped to
   * the most-recent entries by the writer.
   */
  nudged_report_ids?: string[];
}

// Resolved per-call so tests can redirect via $HOME and any runtime HOME change
// (e.g., su -) is picked up. Frozen module-level constants previously made tests
// write to the user's real ~/.wayai/.
function configDir(): string { return path.join(os.homedir(), '.wayai'); }
function configPath(): string { return path.join(configDir(), 'config.json'); }

/**
 * Read the metadata config. Tokens are NOT included — fetch them via
 * `getToken()` from token-store. Returns null only when the user has never
 * logged in (no config file at all).
 */
export function readConfig(): CliConfig | null {
  if (!fs.existsSync(configPath())) return null;

  try {
    const content = fs.readFileSync(configPath(), 'utf-8');
    const parsed = JSON.parse(content);
    if (!parsed.api_url) return null;
    return {
      api_url: parsed.api_url,
      token_expires_at: parsed.token_expires_at,
      nudged_report_ids: Array.isArray(parsed.nudged_report_ids)
        ? parsed.nudged_report_ids.filter((x: unknown): x is string => typeof x === 'string')
        : undefined,
    };
  } catch {
    return null;
  }
}

export function writeConfig(config: CliConfig): void {
  if (!fs.existsSync(configDir())) {
    fs.mkdirSync(configDir(), { recursive: true });
  }

  // Preserve any existing metadata fields (e.g., user_id) that may be added later.
  let existing: Record<string, unknown> = {};
  if (fs.existsSync(configPath())) {
    try {
      existing = JSON.parse(fs.readFileSync(configPath(), 'utf-8'));
    } catch { /* corrupt — overwrite */ }
  }
  // Drop any legacy plaintext token fields that may still be present.
  delete existing.token;
  delete existing.access_token;
  delete existing.refresh_token;

  Object.assign(existing, config);

  fs.writeFileSync(configPath(), JSON.stringify(existing, null, 2) + '\n', {
    mode: 0o600, // owner-only read/write
  });
}
